import { useEffect, useState } from 'react'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { disablePush, enablePush, pushState, type PushState } from '../lib/push.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Button, Label, Paragraph } from './ui.tsx'

/** Zapnuti upozorneni na akce ze sablony v tomhle zarizeni. */
export function PushSettings() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    let live = true
    pushState()
      .then((value) => {
        if (live) setState(value)
      })
      .catch(() => {
        if (live) setState('unsupported')
      })
    return () => {
      live = false
    }
  }, [])

  async function run(action: () => Promise<string | null>) {
    setBusy(true)
    setMessage(null)
    try {
      const text = await action()
      if (text) setMessage({ ok: true, text })
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof HttpError && err.isOffline ? 'Upozornění jde nastavit jen se signálem.' : errorMessage(err),
      })
    } finally {
      setBusy(false)
    }
  }

  if (state === null) return null

  return (
    <div className="stack-8">
      <Label>Upozornění na akce</Label>
      <Paragraph size="meta">
        Když se položka ze šablony objeví v letáku, dáme vědět. Platí pro tohle zařízení; na iPhonu jen v aplikaci
        přidané na plochu.
      </Paragraph>
      {state === 'unsupported' && <Paragraph size="meta">Tady upozornění nefungují.</Paragraph>}
      {state === 'denied' && (
        <Paragraph size="meta" tone="fg">
          Upozornění jsou pro tuhle stránku v prohlížeči zakázaná. Povol je v nastavení webu a zkus to znovu.
        </Paragraph>
      )}
      {state === 'off' && (
        <Button
          className="self-start"
          icon="alert"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const next = await enablePush()
              setState(next)
              return next === 'on' ? 'Upozornění jsou zapnutá.' : null
            })
          }
        >
          Zapnout upozornění
        </Button>
      )}
      {state === 'on' && (
        <div className="button-row wrap-row">
          <Button
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = await api.push.test()
                return result.sent > 0 ? 'Zkušební upozornění je na cestě.' : 'Na serveru není žádný odběr, zkus upozornění vypnout a zapnout.'
              })
            }
          >
            Poslat zkušební
          </Button>
          <Button
            kind="danger"
            compact
            disabled={busy}
            onClick={() =>
              void run(async () => {
                setState(await disablePush())
                return 'Upozornění jsou vypnutá.'
              })
            }
          >
            Vypnout
          </Button>
        </div>
      )}
      {message && <Paragraph tone={message.ok ? 'fg' : 'error'}>{message.text}</Paragraph>}
    </div>
  )
}
